import React, { useState, useMemo } from 'react';
import { Pill, AlertCircle, Clock, Camera, Heart, Info, CheckCircle2, Siren, Check } from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA GLOBAL (Ruta Absoluta)
import { useGlobal } from 'context/GlobalContext';

const RadarSalud = () => {
  const { ninos } = useGlobal();
  
  // ESTADO: Registro local de acciones realizadas durante el turno
  const [atencionesRealizadas, setAtencionesRealizadas] = useState({});

  // CONFIGURACIÓN (En v3.1 vendrá del perfil del maestro logueado)
  const grupoNombre = 'PÁRVULOS B';

  // --- LÓGICA DE FILTRADO REAL ---
  const ninosConCuidados = useMemo(() => {
    return ninos
      .filter(n => n.salon === grupoNombre && n.estatus === 'en_salon' && (n.especial || n.alerta))
      .map(n => ({
        id: n.id,
        nombre: n.nombre,
        tipo: n.alerta?.toLowerCase().includes('medic') ? 'Medicación' : (n.especial ? 'Sensorial' : 'Alergia'),
        detalle: n.alerta || 'Protocolo Especial Activo',
        hora: n.horaMedicina || null,
        instruccion: n.instruccionesCuidado || 'Verificar ficha de seguridad o consultar en recepción.',
        urgente: n.especial,
        fotoMedicina: n.fotoMedicina || null
      }));
  }, [ninos]);

  const confirmarAtencion = (id) => {
    setAtencionesRealizadas(prev => ({
      ...prev,
      [id]: true
    }));
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-8 animate-in fade-in duration-500 pb-20 px-4 font-sans text-left">
      
      {/* ENCABEZADO DE RADAR */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-600 px-6 py-2 rounded-full mb-2 border border-rose-200 shadow-sm">
          <Siren size={18} className="animate-pulse" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] italic">Radar de Seguridad Médica</span>
        </div>
        <h2 className="text-4xl font-black text-slate-900 italic tracking-tighter uppercase leading-tight">Protocolos Activos</h2>
        <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest italic">{grupoNombre}</p>
      </div>

      {/* LISTA DE FICHAS MÉDICAS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {ninosConCuidados.length === 0 ? (
          <div className="col-span-full py-24 bg-slate-100 rounded-[4rem] border-4 border-dashed border-slate-200 text-center flex flex-col items-center justify-center space-y-4 opacity-40">
             <Heart size={80} strokeWidth={1} />
             <p className="font-black uppercase tracking-widest text-slate-500">Sin alertas médicas en este salón</p>
          </div>
        ) : (
          ninosConCuidados.map((nino) => {
            const completado = atencionesRealizadas[nino.id];
            
            return (
              <div 
                key={nino.id} 
                className={`bg-white rounded-[3.5rem] shadow-xl overflow-hidden border-4 transition-all duration-500 relative ${
                  completado ? 'border-emerald-100 opacity-60 grayscale-[0.5]' : 
                  nino.urgente ? 'border-rose-200 shadow-rose-100' : 'border-slate-50'
                }`}
              >
                {/* Cabecera de la Ficha */}
                <div className={`px-8 py-6 flex items-center justify-between ${
                  completado ? 'bg-emerald-50' :
                  nino.tipo === 'Medicación' ? 'bg-amber-50' : 
                  nino.tipo === 'Sensorial' ? 'bg-indigo-50' : 'bg-rose-50'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md transition-colors ${
                      completado ? 'bg-emerald-500 text-white' :
                      nino.tipo === 'Medicación' ? 'bg-amber-500 text-white' : 
                      nino.tipo === 'Sensorial' ? 'bg-indigo-500 text-white' : 'bg-rose-500 text-white'
                    }`}>
                      {completado ? <Check size={24} /> :
                       nino.tipo === 'Medicación' ? <Pill size={24} /> : 
                       nino.tipo === 'Sensorial' ? <Heart size={24} /> : <AlertCircle size={24} />}
                    </div>
                    <span className={`text-[10px] font-black uppercase tracking-widest ${completado ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {completado ? 'Atención Realizada' : nino.tipo}
                    </span>
                  </div>
                  {nino.hora && !completado && (
                    <div className="bg-white/60 backdrop-blur-sm px-4 py-2 rounded-xl flex items-center gap-2 text-amber-600 font-black text-[10px] shadow-sm italic">
                      <Clock size={16} />
                      <span>{nino.hora}</span>
                    </div>
                  )}
                </div>

                <div className="p-10 space-y-6">
                  <div className="text-left">
                    <h3 className="text-3xl font-black text-slate-900 leading-none italic tracking-tighter uppercase mb-2">{nino.nombre}</h3>
                    <p className={`text-xl font-bold italic leading-tight uppercase ${completado ? 'text-slate-400' : 'text-indigo-600'}`}>
                      {nino.detalle}
                    </p>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-[2rem] border-2 border-slate-100 relative overflow-hidden text-left">
                    <Info size={80} className="absolute -right-6 -bottom-6 text-slate-200 opacity-30" />
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 italic">Protocolo de Staff:</h4>
                    <p className="text-base font-bold text-slate-700 leading-relaxed relative z-10 italic">
                      "{nino.instruccion}"
                    </p>
                  </div>

                  {/* Acciones */}
                  {!completado && (
                    <div className="flex flex-col sm:flex-row gap-4 pt-2">
                      <button 
                        onClick={() => nino.fotoMedicina ? window.open(nino.fotoMedicina) : alert("Sin evidencia fotográfica")}
                        className="flex-1 py-5 bg-[#0A2440] text-white rounded-[2rem] font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 active:scale-95 shadow-xl"
                      >
                        <Camera size={18} className="text-indigo-400" /> Ver Medicina
                      </button>
                      
                      <button 
                        onClick={() => confirmarAtencion(nino.id)}
                        className="flex-[1.5] py-5 bg-emerald-500 text-white rounded-[2rem] font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 active:scale-95 shadow-xl border-b-4 border-emerald-700"
                      >
                        <CheckCircle2 size={18} /> Confirmar Aplicación
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* BANNER DE RESPONSABILIDAD */}
      <div className="bg-slate-900 p-8 rounded-[3.5rem] border-b-8 border-indigo-600 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-2xl" />
        <div className="flex items-start gap-5 relative z-10 text-left">
          <div className="p-3 bg-amber-400 rounded-2xl text-slate-900 shadow-lg shrink-0">
            <AlertCircle size={28} />
          </div>
          <div>
            <p className="text-sm font-black text-white uppercase tracking-widest italic">Responsabilidad Sanitaria</p>
            <p className="text-[10px] text-slate-400 font-bold leading-relaxed uppercase tracking-widest mt-2 italic">
              Verifica siempre la identidad del explorador y el nombre en el fármaco antes de la aplicación. 
              <span className="text-indigo-400 ml-1">Cualquier reacción debe ser reportada de inmediato al Admin.</span>
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default RadarSalud;